import React, { useEffect, useMemo } from "react";

import { compact, isEmpty, map } from "lodash/fp";
import { useSearchParams } from "react-router-dom";

import FloatingNav, {
  FloatingNavActions,
  FloatingNavActionDivider,
  FloatingNavInnerContainer,
  FloatingNavTab,
  FloatingNavTabs,
} from "@app/analytics/components/reports/FloatingNav";
import useEditReport from "@app/analytics/MeetingReport/hooks/useEditReport";
import useSearch from "@app/analytics/MeetingReport/hooks/useSearch";
import MeetingReportSearchBox from "@app/analytics/MeetingReport/MeetingReportSearchBox";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useTranscriptStore from "@app/analytics/MeetingReport/stores/transcript";
import type { RecapSection } from "@app/analytics/MeetingReport/types";
import type { SearchOptions } from "@app/analytics/MeetingReport/utils";
import useResponsiveView from "@app/common/hooks/useResponsiveView";

import Stack from "@rui/Stack";

import CopyNotes from "./CopyNotes";
import EditButton from "./EditButton";
import EditMenu from "./EditMenu";
import MeetingReportRecapDialogs from "./MeetingReportRecapDialogs";
import MobileSubNavMenu from "./MobileSubNavMenu";
import RecapSettings from "./RecapSettings";
import RestoreMenu from "./RestoreMenu";
import SearchNavAction from "./SearchNavAction";
import TranscriptFilters from "./TranscriptFilters";

interface MeetingReportRecapSubnavProps {
  copyContent: string;
  isNotetakerReport: boolean;
  isRestrictedReport: boolean;
  onSectionSelect: (section: RecapSection, scrollToSection: boolean) => void;
  selectedSection: RecapSection;
}

const SUBNAV_TABS: {
  value: RecapSection;
  label: string;
  includeOnNotetaker: boolean;
  mobileOnly: boolean;
}[] = [
  {
    value: "notes",
    label: "Notes",
    includeOnNotetaker: true,
    mobileOnly: false,
  },
  {
    value: "transcript",
    label: "Transcript",
    includeOnNotetaker: false,
    mobileOnly: false,
  },
  {
    value: "highlights",
    label: "Highlights",
    includeOnNotetaker: false,
    mobileOnly: true,
  },
  {
    value: "chapters",
    label: "Chapters",
    includeOnNotetaker: false,
    mobileOnly: true,
  },
];

const MeetingReportRecapSubnav = ({
  copyContent,
  isNotetakerReport,
  isRestrictedReport,
  onSectionSelect,
  selectedSection,
}: MeetingReportRecapSubnavProps) => {
  const { inDesktopView } = useResponsiveView();
  const [queryParams] = useSearchParams();
  const searchQueryParam = queryParams.get("search");

  const { enterEditMode } = useEditReport();
  const { searchMode, setSearchMode, clearSearch } = useSearch();

  const { editAccessLevel, inEditMode } = useEditStore((state) => ({
    editAccessLevel: state.editAccessLevel,
    inEditMode: state.editingSection != null,
  }));

  const { allSpeakers, filteredSpeakers, transcriptAvailable } =
    useTranscriptStore((state) => ({
      allSpeakers: state.allSpeakers,
      filteredSpeakers: state.filteredSpeakers,
      transcriptAvailable: state.transcriptAvailable,
    }));

  const searchOpen = Boolean(searchMode);

  useEffect(() => {
    if (!searchQueryParam) return;
    setSearchMode(inEditMode ? "replace" : "search");
  }, [searchMode, inEditMode, searchQueryParam]);

  const handleTabChange = (
    _event: React.SyntheticEvent,
    section: RecapSection
  ) => {
    if (searchOpen && section !== "notes" && section !== "transcript") {
      // close the search box if switching to a tab that does not support search
      clearSearch();
    }
    onSectionSelect(section, true);
  };

  const handleSelectEditSection: typeof enterEditMode = (section) => {
    enterEditMode(section);
    onSectionSelect(section, selectedSection !== section);
  };

  const searchOptions: SearchOptions = useMemo(
    () => ({
      speakers: compact(map("name", filteredSpeakers)),
      appendQueryParam: searchMode !== "replace",
      searchType: searchMode === "replace" ? "manual" : undefined,
    }),
    [filteredSpeakers, searchMode]
  );

  const interactionsEnabled = !isRestrictedReport;

  const showCopyNotes = selectedSection === "notes";
  const showTranscriptFilters =
    selectedSection === "transcript" &&
    transcriptAvailable &&
    !isEmpty(allSpeakers);

  const canEnterEditMode = editAccessLevel !== "none";
  const canRestoreReport = editAccessLevel === "full";

  return (
    <>
      <FloatingNav breakpoint={540}>
        <FloatingNavInnerContainer>
          {inEditMode ? (
            <EditMenu />
          ) : (
            <>
              <FloatingNavTabs
                value={selectedSection}
                onChange={handleTabChange}
              >
                {SUBNAV_TABS.map((tab) => {
                  const shouldHideTab =
                    (isNotetakerReport && !tab.includeOnNotetaker) ||
                    (inDesktopView && tab.mobileOnly);
                  if (shouldHideTab) return null;
                  return (
                    <FloatingNavTab
                      key={tab.value}
                      label={tab.label}
                      value={tab.value}
                    />
                  );
                })}
              </FloatingNavTabs>
              {interactionsEnabled && (
                <>
                  {/* Secret clickable box to handle anyone who thinks that the empty space between the tabs
              and the buttons is the search box */}
                  <Stack
                    flex={1}
                    sx={{
                      height: "100%",
                      display: inDesktopView ? undefined : "none",
                    }}
                    onClick={() => setSearchMode("search")}
                  />
                  <FloatingNavActions spacing={0.5}>
                    {inDesktopView ? (
                      <>
                        <SearchNavAction variant="button" />
                        <RecapSettings variant="menu" />
                        {showCopyNotes && (
                          <>
                            <FloatingNavActionDivider />
                            <CopyNotes variant="button" content={copyContent} />
                          </>
                        )}
                        {showTranscriptFilters && (
                          <>
                            <FloatingNavActionDivider />
                            <TranscriptFilters variant="menu" />
                          </>
                        )}
                        {canEnterEditMode && (
                          <EditButton
                            editAccessLevel={editAccessLevel}
                            onClickEditSection={handleSelectEditSection}
                          />
                        )}
                        {canRestoreReport && <RestoreMenu />}
                      </>
                    ) : (
                      <MobileSubNavMenu
                        onEnterEditMode={
                          canEnterEditMode ? handleSelectEditSection : undefined
                        }
                        notesCopyContent={
                          showCopyNotes ? copyContent : undefined
                        }
                        selectedSection={selectedSection}
                        showRestore={canRestoreReport}
                        showSettings={transcriptAvailable}
                        showTranscriptFilters={showTranscriptFilters}
                      />
                    )}
                  </FloatingNavActions>
                </>
              )}
            </>
          )}
          <MeetingReportSearchBox
            onClose={clearSearch}
            open={searchOpen}
            searchOptions={searchOptions}
          />
        </FloatingNavInnerContainer>
      </FloatingNav>

      <MeetingReportRecapDialogs />
    </>
  );
};

export default MeetingReportRecapSubnav;
